import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [othersOpen, setOthersOpen] = useState(false);
  const [gstOpen, setGstOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
  ];

  const gstLinks = [
    { name: 'GST Registration', path: '/gst-registration' },
    { name: 'GST Filing', path: '/gst-filing' },
    { name: 'ITR Filing', path: '/itr-filing' },
  ];

  const calculatorLinks = [
    { name: 'Income Tax Calculator', path: '/income-tax-calculator' },
    { name: 'TDS Calculator', path: '/tds-calculator' },
    { name: 'GST Late Fees Calculator', path: '/gst-late-fees-calculator' },
  ];

  const othersLinks = [
    { name: 'Business Registration', path: '/business-registration' },
    { name: 'Other Services', path: '/other-services' },
  ];

  const isActive = (path) => location.pathname === path;
  const isGstActive = gstLinks.some(link => location.pathname === link.path);
  const isCalculatorActive = calculatorLinks.some(link => location.pathname === link.path);
  const isOthersActive = othersLinks.some(link => location.pathname === link.path);

  return (
    <nav 
      data-testid="navbar"
      className="fixed top-0 w-full z-50 backdrop-blur-xl bg-navy-900/90 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="nav-logo"
            className="flex items-center gap-2 group"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">T</span>
            </div>
            <span className="font-heading font-bold text-xl text-white group-hover:text-blue-400 transition-colors">
              Tax<span className="text-blue-400">Company</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`font-body font-medium transition-colors text-sm ${
                  isActive(link.path)
                    ? 'text-blue-400'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* GST Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setGstOpen(true)}
              onMouseLeave={() => setGstOpen(false)}
            >
              <button
                data-testid="nav-gst-dropdown"
                className={`flex items-center gap-1 font-body font-medium transition-colors text-sm py-2 ${
                  isGstActive ? 'text-blue-400' : 'text-slate-300 hover:text-white'
                }`}
              >
                GST
                <ChevronDown className={`w-4 h-4 transition-transform ${gstOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* GST Dropdown Menu */}
              {gstOpen && (
                <div 
                  data-testid="gst-dropdown-menu"
                  className="absolute top-full left-0 pt-2"
                >
                  <div className="w-48 bg-navy-800 border border-white/10 rounded-xl shadow-xl overflow-hidden">
                    {gstLinks.map((link, index) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`gst-link-${index}`}
                        className={`block px-4 py-3 text-sm transition-colors border-b border-white/5 last:border-0 ${
                          isActive(link.path)
                            ? 'bg-blue-500/10 text-blue-400 border-l-2 border-l-blue-500'
                            : 'text-slate-300 hover:bg-navy-700 hover:text-white'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Tax Calculators Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setCalculatorOpen(true)}
              onMouseLeave={() => setCalculatorOpen(false)}
            >
              <button
                data-testid="nav-calculator-dropdown"
                className={`flex items-center gap-1 font-body font-medium transition-colors text-sm py-2 ${
                  isCalculatorActive ? 'text-blue-400' : 'text-slate-300 hover:text-white'
                }`}
              >
                Tax Calculators
                <ChevronDown className={`w-4 h-4 transition-transform ${calculatorOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {calculatorOpen && (
                <div 
                  data-testid="calculator-dropdown-menu"
                  className="absolute top-full left-0 pt-2"
                >
                  <div className="w-56 bg-navy-800 border border-white/10 rounded-xl shadow-xl overflow-hidden">
                    {calculatorLinks.map((link, index) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`calc-link-${index}`}
                        className={`block px-4 py-3 text-sm transition-colors border-b border-white/5 last:border-0 ${
                          isActive(link.path)
                            ? 'bg-blue-500/10 text-blue-400 border-l-2 border-l-blue-500'
                            : 'text-slate-300 hover:bg-navy-700 hover:text-white'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Others Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setOthersOpen(true)}
              onMouseLeave={() => setOthersOpen(false)}
            >
              <button
                data-testid="nav-others-dropdown"
                className={`flex items-center gap-1 font-body font-medium transition-colors text-sm py-2 ${
                  isOthersActive ? 'text-blue-400' : 'text-slate-300 hover:text-white'
                }`}
              >
                Others
                <ChevronDown className={`w-4 h-4 transition-transform ${othersOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Others Dropdown Menu */}
              {othersOpen && (
                <div 
                  data-testid="others-dropdown-menu"
                  className="absolute top-full left-0 pt-2"
                >
                  <div className="w-56 bg-navy-800 border border-white/10 rounded-xl shadow-xl overflow-hidden">
                    {othersLinks.map((link, index) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        data-testid={`others-link-${index}`}
                        className={`block px-4 py-3 text-sm transition-colors border-b border-white/5 last:border-0 ${
                          isActive(link.path)
                            ? 'bg-blue-500/10 text-blue-400 border-l-2 border-l-blue-500'
                            : 'text-slate-300 hover:bg-navy-700 hover:text-white'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              data-testid="nav-link-about"
              className={`font-body font-medium transition-colors text-sm ${
                isActive('/about') ? 'text-blue-400' : 'text-slate-300 hover:text-white'
              }`}
            >
              About
            </Link>
            <Link
              to="/contact"
              data-testid="nav-link-contact"
              className={`font-body font-medium transition-colors text-sm ${
                isActive('/contact') ? 'text-blue-400' : 'text-slate-300 hover:text-white'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* CTA & Contact */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="tel:+919717705640" 
              data-testid="nav-phone"
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                <Phone className="w-4 h-4 text-blue-400" />
              </div>
              <div className="text-xs">
                <p className="text-blue-400 font-medium">Call 24/7</p>
                <p className="text-white">+91-9717705640</p>
              </div>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-white"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div 
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/5 bg-navy-900/95 backdrop-blur-xl max-h-[calc(100vh-80px)] overflow-y-auto"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                onClick={() => setIsOpen(false)}
                className={`block py-2 font-medium transition-colors ${
                  isActive(link.path) ? 'text-blue-400' : 'text-slate-300 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Mobile GST Links */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <p className="text-blue-400 font-medium text-sm mb-2">GST</p>
              {gstLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 pl-4 font-medium transition-colors text-sm ${
                    isActive(link.path) ? 'text-blue-400' : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Mobile Calculator Links */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <p className="text-blue-400 font-medium text-sm mb-2">Tax Calculators</p>
              {calculatorLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 pl-4 font-medium transition-colors text-sm ${
                    isActive(link.path) ? 'text-blue-400' : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Mobile Others Links */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <p className="text-blue-400 font-medium text-sm mb-2">Others</p>
              {othersLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 pl-4 font-medium transition-colors text-sm ${
                    isActive(link.path) ? 'text-blue-400' : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            <Link
              to="/about"
              onClick={() => setIsOpen(false)}
              className={`block py-2 font-medium transition-colors ${
                isActive('/about') ? 'text-blue-400' : 'text-slate-300 hover:text-white'
              }`}
            >
              About
            </Link>
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className={`block py-2 font-medium transition-colors ${
                isActive('/contact') ? 'text-blue-400' : 'text-slate-300 hover:text-white'
              }`}
            >
              Contact
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};
